// components/atoms/InputText.tsx

import { FC, useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface InputTextProps extends React.InputHTMLAttributes<HTMLInputElement> {
  iconLeft?: React.ReactNode;
  suffix?: React.ReactNode;
  error?: boolean;
  className?: string;
}

export const Input: FC<InputTextProps> = ({
  iconLeft,
  suffix,
  disabled,
  error = false,
  type = 'text',
  className = '',
  placeholder = '',
  ...props
}) => {
  const isPassword = type === 'password';
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const hasLeftIcon = Boolean(iconLeft);
  const hasSuffix = Boolean(suffix);

  const resolvedType = isPassword
    ? isPasswordVisible
      ? 'text'
      : 'password'
    : type;

  return (
    <div className={`relative w-full ${hasSuffix ? 'flex' : ''}`}>
      <input
        type={resolvedType}
        className={`block w-full border p-2 ${hasLeftIcon ? 'pl-10' : 'pl-4'} placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:text-sm
             text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${
               hasSuffix ? 'rounded-l-md' : 'rounded-md'
             } ${
               disabled
                 ? '!bg-gray-100 dark:!bg-gray-700 !cursor-not-allowed !text-gray-500 dark:!text-gray-400'
                 : ''
             } ${
               error
                 ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                 : 'border-gray-300 dark:border-gray-600'
             } [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:dark:invert [&::-webkit-calendar-picker-indicator]:dark:brightness-0 [&::-webkit-calendar-picker-indicator]:dark:contrast-200 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden ${
               isPassword ? 'pr-10' : ''
             } ${className}`}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
      {iconLeft && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">
          {iconLeft}
        </div>
      )}
      {hasSuffix && (
        <span className={`inline-flex items-center px-3 py-2 border border-l-0 rounded-r-md text-sm ${
          error
            ? 'border-red-500 bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
        }`}>
          {suffix}
        </span>
      )}
      {isPassword && (
        <div
          role="button"
          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          onClick={() => setIsPasswordVisible((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsPasswordVisible((prev) => !prev);
            }
          }}
          tabIndex={disabled ? -1 : 0}
          className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          style={{ lineHeight: 0, pointerEvents: disabled ? 'none' : 'auto' }}>
          {isPasswordVisible ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </div>
      )}
    </div>
  );
};
